import { useState, useRef, useCallback } from 'react';
import { X, Search } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { searchChannelMessages } from '../../services/chatChannelService';
import { sanitizeHtml } from '../../lib/sanitize';
import Spinner from '../ui/Spinner';

interface Props {
  channelId: string;
  onClose: () => void;
  onJumpToMessage: (messageId: string) => void;
}

export default function ChatSearchPanel({ channelId, onClose, onJumpToMessage }: Props) {
  const [query, setQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [page, setPage] = useState(1);
  const [mentionFilter, setMentionFilter] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();

  const handleChange = useCallback((val: string) => {
    setQuery(val);
    setPage(1);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => setDebouncedQuery(val.trim()), 300);
  }, []);

  const enabled = debouncedQuery.length >= 2;
  const { data, isLoading } = useQuery({
    queryKey: ['chatSearch', channelId, debouncedQuery, page, mentionFilter],
    queryFn: () => searchChannelMessages(channelId, { q: debouncedQuery, page, limit: 20, mentions: mentionFilter ? 'me' : undefined }),
    enabled,
  });

  const formatDate = (iso: string) => {
    const d = new Date(iso);
    return `${d.getMonth() + 1}/${d.getDate()} ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
  };

  return (
    <div className="bg-white border-b border-gray-200 shadow-sm">
      {/* Search input */}
      <div className="flex items-center gap-2 px-4 py-2.5">
        <Search className="size-4 text-gray-400 shrink-0" />
        <input value={query} onChange={(e) => handleChange(e.target.value)} autoFocus
          placeholder="메시지 검색..."
          className="flex-1 text-sm outline-none bg-transparent" />
        <button onClick={onClose} className="p-1 text-gray-400 hover:text-gray-600 rounded">
          <X className="size-4" />
        </button>
      </div>
      <div className="px-4 pb-2">
        <label className="flex items-center gap-2 text-sm text-gray-500 cursor-pointer select-none">
          <input type="checkbox" checked={mentionFilter}
            onChange={() => { setMentionFilter((v) => !v); setPage(1); }}
            className="rounded border-gray-300 text-primary-600 focus:ring-primary-500" />
          나를 멘션한 메시지만
        </label>
      </div>

      {/* Results */}
      {query.length > 0 && query.length < 2 && (
        <p className="px-4 py-3 text-sm text-gray-400 border-t border-gray-100">2자 이상 입력해주세요</p>
      )}

      {enabled && isLoading && (
        <div className="flex justify-center py-4 border-t border-gray-100"><Spinner className="size-5" /></div>
      )}

      {enabled && data && (
        <div className="border-t border-gray-100 max-h-[50vh] overflow-y-auto">
          {data.results.length === 0 ? (
            <p className="px-4 py-4 text-sm text-gray-400 text-center">검색 결과가 없습니다</p>
          ) : (
            <>
              <p className="px-4 py-2 text-sm text-gray-500">검색 결과 ({data.pagination.total}건)</p>
              {data.results.map((r) => (
                <button key={r.messageId} onClick={() => onJumpToMessage(r.messageId)}
                  className="w-full text-left px-4 py-3 hover:bg-gray-50 border-b border-gray-50 transition-colors">
                  <div className="flex items-center gap-2 mb-0.5">
                    <span className="text-sm font-medium text-gray-700">{r.sender.name}</span>
                    <span className="text-[11px] text-gray-400">{formatDate(r.createdAt)}</span>
                  </div>
                  <p className="text-sm text-gray-600 line-clamp-2"
                    dangerouslySetInnerHTML={{ __html: sanitizeHtml(r.highlightedContent) }} />
                </button>
              ))}
              {data.pagination.page < data.pagination.totalPages && (
                <button onClick={() => setPage((p) => p + 1)}
                  className="w-full py-2 text-sm text-primary-600 hover:bg-gray-50 transition-colors">
                  더 보기
                </button>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}
